"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ShieldCheck, Globe, Users, Target, Newspaper, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const coverageAreas = ["Berita Nasional", "Berita Daerah", "Pemerintahan", "Hukum", "Ekonomi", "Sosial Kemasyarakatan", "Organisasi", "UMKM", "Teknologi", "Pemuda Pancasila"];
const credibilityStats = [{
  icon: ShieldCheck,
  title: "Jurnalisme Terpercaya",
  description: "Menyajikan fakta yang akurat dan berimbang."
}, {
  icon: Globe,
  title: "Jangkauan Nasional",
  description: "Meliput peristiwa dari pusat hingga ke daerah."
}, {
  icon: Users,
  title: "Suara Masyarakat",
  description: "Menjadi wadah aspirasi komunitas dan organisasi."
}];
export default function AboutAbadiNews() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="about-abadinews" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-lg mb-4">
            <Newspaper className="w-6 h-6 text-primary mr-2" />
            <span className="font-default font-semibold text-primary tracking-wide uppercase text-sm">
              Mengenal Kami
            </span>
          </div>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground">
            Portal Berita Nasional Terpercaya
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            ABADI.NEWS hadir sebagai pilar informasi terdepan, menyajikan berita terkini dan mendalam dari berbagai sektor. Kami berkomitmen untuk menjadi sumber referensi utama bagi masyarakat Indonesia.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {}
          <div ref={contentRef} className={`space-y-8 transition-all duration-700 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <Card className="border-primary/20 shadow-md bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl font-default text-foreground">
                  <Target className="w-6 h-6 text-primary mr-3" />
                  Misi Kami
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  Memberikan jurnalisme berkualitas yang mencerahkan, mengedukasi, dan menginspirasi masyarakat. Kami menjunjung tinggi kode etik jurnalistik dalam setiap peliputan untuk memastikan informasi yang faktual, objektif, dan bermanfaat bagi kemajuan bangsa.
                </p>
              </CardContent>
            </Card>

            <div>
              <h3 className="font-default text-xl font-semibold mb-4 text-foreground">
                Fokus Liputan Kami
              </h3>
              <div className="flex flex-wrap gap-2">
                {coverageAreas.map((area, i) => <Badge key={i} data-index={i} variant="secondary" className="text-sm py-1.5 px-3 bg-secondary/10 hover:bg-secondary/20 text-secondary-foreground transition-colors">
                    {area}
                  </Badge>)}
              </div>
            </div>

            <Separator className="my-6" />

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/CooperationPage">
                  Jalin Kerjasama
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/NewsPage">
                  Baca Berita Terkini
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={visualRef} className={`space-y-8 transition-all duration-700 delay-400 ease-out ${isVisualVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="rounded-xl overflow-hidden shadow-lg border border-border">
              <AspectRatio ratio={16 / 9}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/abadi_news_editorial_team.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/abadi_news_editorial_team.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/abadi_news_editorial_team.png-wvc-sizes" alt="Tim Redaksi Profesional ABADI.NEWS" className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" loading="lazy" />
              </AspectRatio>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {credibilityStats.map((stat, i) => {
              const Icon = stat.icon;
              return <Card key={i} data-index={i} className="border-border shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-6 flex flex-col items-start">
                      <div className="p-3 bg-primary/10 rounded-lg mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h4 className="font-default font-semibold text-foreground mb-2">
                        {stat.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {stat.description}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}